import {
  FAIcon,
  Theme,
  Breakpoints,
  useBreakpoint,
  useTranslation,
  useSession,
  Checkbox,
} from '@nextgen-web-framework/all';
import { formatPrice, formatNumber } from '@nextgen-web-framework/core';
import { useState } from 'react';
import {
  CartEntryDetails,
  ListResponse,
  ListEntry,
  ListType,
  getCategoryColor,
  capitalizeFirstLetter,
  categoryDataType,
  UserListResponse,
  getCategoryNameByCategoryId,
  Cart,
  updateCart,
  updateIsLoading,
  updatedSelectedStateSubCart,
  SubCartDetail,
} from '@commerce-cart-webcomponents/sdk';
import { useDispatch } from 'react-redux';
import { DisplayListType } from '../lists/components/display-list-type/DisplayListType';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { CartImageComponent } from '../../common/components/cart-image-component/CartImageComponent';
import { useFeatureFlag } from '../../common';
import styles from './GCOList.module.scss';
import { GCOErrorList } from './gco-error-list/GCOErrorList';
import { GCOEllipsesMenu } from './gco-ellipses-menu/GCOEllipsesMenu';

interface GCOSubCartsProps {
  subCarts: SubCartDetail[];
  clickedShowCart?: (a: string) => void;
  myLists?: ListResponse[];
  isTypeList?: boolean;
  clickedShowList?: (a: string) => void;
  listUpdatedCb?: (listId: string, isDeleted?: boolean) => void;
  isReceiver?: boolean;
  categoryData?: categoryDataType[];
  totalListCount?: number;
  loadNextPage?: () => void;
  myListsData?: UserListResponse;
}

export const GCOList = ({
  subCarts,
  clickedShowCart,
  myLists,
  isTypeList = false,
  clickedShowList,
  listUpdatedCb,
  isReceiver = false,
  categoryData,
  totalListCount,
  loadNextPage,
  myListsData,
}: Readonly<GCOSubCartsProps>) => {
  const t = useTranslation();
  const dispatch = useDispatch();
  const viewPort = useBreakpoint();
  const noItemText = t('cart.gcoList.noItem');
  const getDesktopMaxBoxNumber = isTypeList ? 10 : 6;
  const getMaxBoxNumber = viewPort === Breakpoints.SM || viewPort === Breakpoints.XS ? 4 : getDesktopMaxBoxNumber;
  const [lastSubscriptionCard, setLastSubscriptionCard] = useState<HTMLElement | null>();
  const { hideBVFlag, cartSelectionFlag } = useFeatureFlag();
  const { isABO, isMember } = useSession();
  const isMemberAboReceiver = isABO() || isMember() || isReceiver;
  const isReceiverNotABO = isReceiver && !isABO();

  const clickActionHandler = (isTypeCart: boolean, itemCart: SubCartDetail, itemList: ListResponse) =>
    isTypeCart ? clickedShowCart?.(itemCart.subCartId as string) : clickedShowList?.(itemList.listId as string);
  const getEntries = (isTypeCart: boolean, itemCart: SubCartDetail, itemList: ListResponse) =>
    isTypeCart ? itemCart?.entries ?? [] : itemList?.payLoad?.entries ?? [];

  const paddingClass = (isTypeCart: boolean) => isTypeCart && styles['adjust-padding'];
  useIntersectionObserver(
    lastSubscriptionCard,
    { rootMargin: '1px' },
    () => {
      const shouldLoadNextPage =
        (myLists?.length ?? 0) < (totalListCount ?? 0) &&
        loadNextPage &&
        (myListsData?.lists?.length ?? 0) < (totalListCount ?? 0);

      if (shouldLoadNextPage) {
        loadNextPage();
      }
    },
    [myLists?.length ?? 0],
  );

  const handleCheckBoxChange = async (subCart: SubCartDetail) => {
    try {
      dispatch(updateIsLoading(true));
      const res = (await updatedSelectedStateSubCart(subCart, !subCart.selected)) as Cart;
      dispatch(updateCart(res));
    } finally {
      dispatch(updateIsLoading(false));
    }
  };

  const renderImages = (isTypeCart: boolean, itemCart: SubCartDetail, itemList: ListResponse) =>
    getEntries(isTypeCart, itemCart, itemList)
      ?.slice(0, getMaxBoxNumber)
      .map((entry: CartEntryDetails | ListEntry, index: number) => (
        <div key={entry.entryId} className={styles['commerce-cart-gco-line-item-entry-container-images-box']}>
          {index === getMaxBoxNumber - 1 && getEntries(isTypeCart, itemCart, itemList)?.length > getMaxBoxNumber - 1 ? (
            <div
              role="presentation"
              aria-label="more-link-button"
              onKeyDown={() => clickActionHandler(isTypeCart, itemCart, itemList)}
              onClick={() => clickActionHandler(isTypeCart, itemCart, itemList)}
              className={`${styles['commerce-cart-gco-line-item-entry-container-images-box-last']} ${''}`}
            >
              <div className={styles['commerce-cart-gco-line-item-entry-container-images-box-last-count']}>
                {'More'}
              </div>
              <CartImageComponent
                src={entry.product.cartIconPublicUrl ?? ''}
                alt={entry.product.cartIconAltText ?? ''}
                loading="lazy"
                onClick={() => clickActionHandler(isTypeCart, itemCart, itemList)}
                onKeyDown={() => clickActionHandler(isTypeCart, itemCart, itemList)}
                className={styles['commerce-cart-gco-line-item-entry-container-images-box-last-image']}
              />
            </div>
          ) : (
            <CartImageComponent
              src={entry.product.cartIconPublicUrl ?? ''}
              alt={entry.product.cartIconAltText ?? ''}
              loading="lazy"
              onClick={() => clickActionHandler(isTypeCart, itemCart, itemList)}
              onKeyDown={() => clickActionHandler(isTypeCart, itemCart, itemList)}
            />
          )}
        </div>
      ));

  const renderEntry = (item: SubCartDetail | ListResponse, isTypeCart: boolean, mainIndex: number) => {
    const itemCart: SubCartDetail = item as SubCartDetail;
    const itemList: ListResponse = item as ListResponse;
    const {
      firstName = '',
      lastName = '',
      coApplicantFirstName = '',
      coApplicantLastName = '',
    } = itemCart?.amwayUser || {};

    const categoryName = getCategoryNameByCategoryId(itemList?.category, categoryData);
    const totalEntries = !item.totalEntries;
    const isLastCard = mainIndex === (myLists?.length ?? 0) - 1;
    return (
      <div
        className={styles['commerce-cart-gco-line-item-entry']}
        data-testid={'gco-card-test-id'}
        ref={isLastCard ? setLastSubscriptionCard : undefined}
      >
        <div
          className={`${styles['commerce-cart-gco-line-item-entry-details']} ${paddingClass(isTypeCart)}`}
          style={{
            backgroundColor: getCategoryColor(
              categoryData as categoryDataType[],
              itemList.category as string,
              isTypeCart,
              true,
            ),
          }}
        >
          <div className={styles['commerce-cart-gco-line-item-entry-details-checkbox-container']}>
            {cartSelectionFlag && isTypeCart && (
              <div className={styles['commerce-cart-gco-line-item-entry-details-checkbox']}>
                <Checkbox
                  disabled={!itemCart?.entries?.length}
                  onClick={() => handleCheckBoxChange(itemCart)}
                  checked={itemCart.selected}
                  text=""
                />
              </div>
            )}

            <div>
              {isTypeCart ? (
                <span>
                  {firstName} {lastName} {coApplicantFirstName && t('cart.cartLineitems.andText')}{' '}
                  {coApplicantFirstName} {coApplicantLastName} (
                  {mainIndex == 0 ? t('cart.gcoList.You') : itemCart?.amwayUser?.accountId})
                </span>
              ) : (
                <span
                  style={{
                    color: getCategoryColor(
                      categoryData as categoryDataType[],
                      itemList.category as string,
                      isTypeCart,
                      false,
                    ),
                  }}
                  className={styles['commerce-cart-gco-line-item-entry-details-listName']}
                  onKeyDown={() => clickActionHandler(isTypeCart, itemCart, itemList)}
                  onClick={() => clickActionHandler(isTypeCart, itemCart, itemList)}
                  tabIndex={0}
                  role="button"
                  aria-label="list-name"
                >
                  {capitalizeFirstLetter(itemList.payLoad?.name as string)}
                </span>
              )}
              <div className={styles['commerce-cart-gco-line-item-entry-details-count']}>
                <span
                  style={{
                    color: getCategoryColor(
                      categoryData as categoryDataType[],
                      itemList.category as string,
                      isTypeCart,
                      false,
                    ),
                  }}
                >
                  {totalEntries
                    ? isTypeCart
                      ? noItemText
                      : t('cart.gcoList.noItemList')
                    : `${item.totalEntries} Item${(item?.totalEntries as number) > 1 ? 's' : ''}`}
                </span>
              </div>
            </div>
          </div>
          <GCOEllipsesMenu
            isReceiver={isReceiver}
            isTypeCart={isTypeCart}
            itemCart={itemCart}
            itemList={itemList}
            mainIndex={mainIndex as number}
            listUpdatedCb={listUpdatedCb}
          />
        </div>
        <div className={`${styles['commerce-cart-gco-line-item-entry-container']} ${paddingClass(isTypeCart)}`}>
          {itemList.listType != ListType.Wishlist && (
            <div className={styles['commerce-cart-gco-line-item-entry-container-count']}>
              <DisplayListType
                isDisplayOnMyShop={itemList.isDisplayedOnShop || false}
                isPrivate={itemList.listType === ListType.Private}
                listCategory={categoryName}
                showListType={!isTypeCart}
              />
            </div>
          )}
          {(!!itemCart?.entries?.length || isTypeList) && (
            <>
              <GCOErrorList isTypeCart={isTypeCart} itemCart={itemCart} mainIndex={mainIndex as number} />
              <div className={styles['commerce-cart-gco-line-item-entry-container-images']}>
                {renderImages(isTypeCart, itemCart, itemList)}
              </div>
            </>
          )}

          {!isTypeCart && itemList.listType != ListType.Wishlist && (
            <div className={styles['commerce-cart-gco-line-item-entry-container-description']}>
              {itemList.payLoad?.description}
            </div>
          )}
          <div className={styles['commerce-cart-gco-line-item-entry-container-bottom']}>
            <div>
              <div className={styles['commerce-cart-gco-line-item-entry-container-price']}>
                {formatPrice((item?.totalPrice?.value as number) ?? 0)}
              </div>
              {isMemberAboReceiver && (
                <div
                  className={`${
                    isMember() || isReceiverNotABO
                      ? styles['commerce-cart-gco-line-item-entry-container-pvbv-member']
                      : styles['commerce-cart-gco-line-item-entry-container-pvbv']
                  }`}
                >
                  {isABO() && (
                    <div>
                      {hideBVFlag
                        ? `${t('cart.cartTotal.pv')} ${formatNumber(item?.totalPrice?.amwayValue?.pointValue ?? 0)} `
                        : t('cart.cartTotal.pvbvTablet')
                            .replace(/{{pointValue}}/g, formatNumber(item?.totalPrice?.amwayValue?.pointValue ?? 0))
                            .replace(
                              /{{businessVolume}}/g,
                              formatNumber(item?.totalPrice?.amwayValue?.businessVolume ?? 0),
                            )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div
              tabIndex={0}
              role="button"
              onKeyDown={() => clickActionHandler(isTypeCart, itemCart, itemList)}
              className={styles['commerce-cart-gco-line-item-entry-container-pvbv-icon']}
              onClick={() => clickActionHandler(isTypeCart, itemCart, itemList)}
              aria-label="angle-right"
              data-testid="right-test-id"
            >
              <FAIcon iconName="angle-right" theme={Theme.BLACK} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles['commerce-cart-gco-line-item']}>
      {!isTypeList
        ? subCarts.map((cart, mainIndex) => renderEntry(cart, true, mainIndex))
        : myLists?.map((list, index) => renderEntry(list, false, index))}
    </div>
  );
};

export type { GCOSubCartsProps as GCOListProps };
